'use client';

import { Container } from '@/components/layout/container/container';
import { AccordionFeat } from '@/components/ui/accordion-feat/accordion-feat';
import { Badge } from '@/components/ui/badge/badge';
import LottieComponent from '@/components/ui/card-with-image/lottie-component';
import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import { Media } from '@/components/ui/media/media';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import type { MediaData } from '@/lib/getMediaData';
import type { SectionBgVariant } from '@/lib/types/misc';
import { cn } from '@/lib/utils';
import type { Document } from '@contentful/rich-text-types';
import { HTMLAttributes, ReactNode, useState } from 'react';
import styles from './tab-section.module.scss';

export type TabSectionItem = {
  id: string;
  title: string;
  description: string;
  icon?: ReactNode;
  image?: string;
  imageAlt?: string;
  imageAspectRatio?: '1:1' | '4:5' | '16:9';
  imageFit?: 'contain' | 'cover';
  mediaData?: MediaData;
  cta?: ReactNode;
};

export type TabGroup = {
  id: string;
  title: string;
  icon?: ReactNode;
  items: TabSectionItem[];
};

export type TabSectionProps = {
  id?: string;
  badge?: string;
  title?: string;

  description?: Document;
  tabGroups?: TabGroup[];
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string;
  backgroundColorFrom?: string;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  isBackgroundDark?: boolean;
  headerAlignment?: 'left' | 'center' | 'right';
  cta?: ReactNode;
};

export const TabSection = ({
  id,
  badge,
  title,

  description,
  tabGroups = [],
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  showDecorativeLines = false,
  showDecorativeDots = false,
  isBackgroundDark = false,
  headerAlignment = 'left',
  cta,
  className,
  ...props
}: TabSectionProps & HTMLAttributes<HTMLElement>) => {
  const firstGroupId = tabGroups[0]?.id ?? null;
  const [activeGroupId, setActiveGroupId] = useState<string | null>(firstGroupId);

  const resolvedItems =
    tabGroups.find((g) => g.id === activeGroupId)?.items ?? tabGroups[0]?.items ?? [];

  const [activeItem, setActiveItem] = useState<string | null>(resolvedItems[0]?.id ?? null);

  const handleGroupChange = (groupId: string) => {
    setActiveGroupId(groupId);
    const newItems = tabGroups.find((g) => g.id === groupId)?.items ?? [];
    setActiveItem(newItems[0]?.id ?? null);
  };

  const activeItemData = resolvedItems.find((item) => item.id === activeItem);
  const activeImage = activeItemData?.image;
  const activeMediaData = activeItemData?.mediaData;
  const hasActiveVisual = !!activeMediaData || !!activeImage;

  const accordionItems = resolvedItems.map((item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    icon: item.icon,
    image: item.image,
    imageAlt: item.imageAlt,
    imageAspectRatio: item.imageAspectRatio,
    imageFit: item.imageFit,
    mediaData: item.mediaData,
    cta: item.cta,
  }));

  return (
    <section
      id={id}
      className={cn(
        styles.component,
        styles['component--with-padding'],
        styles[`component--${backgroundVariant}`],
        isBackgroundDark && styles['component--dark'],
        className,
      )}
      style={
        backgroundVariant === 'custom' && backgroundColorFrom
          ? { background: backgroundColorFrom }
          : undefined
      }
      {...props}
    >
      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.component_background}>
          <Image
            src={backgroundImage}
            alt=""
            className={styles.component_background_image}
            fill={true}
            sizes="100vw"
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        </div>
      )}
      {(showDecorativeLines || showDecorativeDots) && (
        <DecorativeLines
          showDots={showDecorativeDots}
          showLines={showDecorativeLines}
          variant={isBackgroundDark ? 'dark' : 'light'}
        />
      )}
      <Container className={styles.component_container}>
        <div
          className={cn(
            styles.component_header,
            headerAlignment !== 'left' && styles[`component_header--${headerAlignment}`],
          )}
        >
          {badge && <Badge text={badge} />}
          <div className={styles.component_header_text}>
            {title &&
              (() => {
                const Tag = 'h2' as const;
                return <Tag>{title}</Tag>;
              })()}
            {description && (
              <div
                className={
                  isBackgroundDark
                    ? styles['component_header_description--dark']
                    : styles['component_header_description--light']
                }
              >
                <RichTextViewer content={description} dark={isBackgroundDark} />
              </div>
            )}
          </div>
          {cta && <div className={styles.component_cta}>{cta}</div>}
        </div>

        <div className={styles.component_tabs_section}>
          {tabGroups.length >= 2 && (
            <div className={styles.group_selector} data-count={tabGroups.length}>
              {tabGroups.map((group) => (
                <button
                  key={group.id}
                  className={cn(
                    styles.group_tab,
                    group.id === activeGroupId && styles['group_tab--active'],
                  )}
                  onClick={() => handleGroupChange(group.id)}
                >
                  {group.icon && <span className={styles.group_tab__icon}>{group.icon}</span>}
                  <span>{group.title}</span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.component_body}>
            <div className={styles.component_tabs}>
              <AccordionFeat
                key={activeGroupId ?? 'default'}
                items={accordionItems}
                defaultOpen={resolvedItems[0]?.id}
                onItemChange={setActiveItem}
              />
            </div>
            {hasActiveVisual && (
              <div
                className={styles.component_image}
                style={{
                  aspectRatio: activeItemData?.imageAspectRatio
                    ? activeItemData.imageAspectRatio.replace(':', ' / ')
                    : undefined,
                  overflow: 'hidden',
                }}
              >
                {activeMediaData ? (
                  <Media {...activeMediaData} className="h-full w-full" width={0} height={0} />
                ) : activeImage && /\.lottie(\?|$)/i.test(activeImage) ? (
                  <LottieComponent src={activeImage} className="h-full w-full" />
                ) : activeImage ? (
                  <Image
                    src={activeImage}
                    alt={activeItemData?.imageAlt || activeItemData?.title || ''}
                    width={0}
                    height={0}
                    sizes="100%"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      objectFit: activeItemData?.imageFit ?? 'cover',
                      objectPosition: 'center',
                      aspectRatio: activeItemData?.imageAspectRatio
                        ? activeItemData.imageAspectRatio.replace(':', ' / ')
                        : undefined,
                    }}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};
